import { useEffect, useRef } from 'react';
import { SignupFlow } from 'corgi-types';
import type { StringMap } from 'corgi-types';

import { useLocalization } from '../providers/LocaleProvider';
import { useRouter } from '../providers/RouterProvider';
import { getQueryParamsString } from '../utils/url';

type SignupRedirect =
  | {
      userFlow: SignupFlow;
    }
  | { redirectUrl: string; useRouter?: boolean };

const REDIRECT_DELAY = 300;

const REDIRECT_TRANSLATION_KEYS = new Map([
  [SignupFlow.MyAccount, 'h24_my_overview_url'],
  [SignupFlow.Cart, 'h24_cart_url'],
  [SignupFlow.Contact, 'h24_contact_redirect_url'],
  [SignupFlow.Wishlist, 'h24_wishlist_url'],
  [SignupFlow.ContentHomeClub, 'h24_home_club_redirect_url'],
  [SignupFlow.CartHomeClub, 'h24_cart_url'],
  [SignupFlow.GiftCardCheckout, 'h24_single_page_digital_checkout_url'],
  [SignupFlow.Checkout, 'h24_single_page_checkout_url'],
]);
const FALLBACK_REDIRECT_KEY = 'h24_my_overview_url';

const useCancellableTimeout = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return (callback: () => void, delay: number) => {
    timeoutRef.current = setTimeout(callback, delay);
  };
};

export const useRedirectAfterDelay = (args: SignupRedirect) => {
  const { p } = useLocalization();
  const router = useRouter();
  const scheduleTimeout = useCancellableTimeout();

  return ({ queryParams }: { queryParams?: StringMap } = {}) => {
    scheduleTimeout(() => {
      if ('redirectUrl' in args) {
        if (args.useRouter) {
          router.push(args.redirectUrl);
        } else {
          window.location.href = args.redirectUrl;
        }
      } else {
        const { userFlow } = args;
        if (userFlow === SignupFlow.Redirect) {
          window.location.reload();

          return;
        }

        const translationKey = REDIRECT_TRANSLATION_KEYS.get(userFlow) || FALLBACK_REDIRECT_KEY;
        let redirectUrl = p.t(translationKey);
        if (queryParams) {
          redirectUrl = `${redirectUrl}?${getQueryParamsString(queryParams)}`;
        }

        if ([SignupFlow.Checkout, SignupFlow.GiftCardCheckout].includes(userFlow)) {
          router.push(redirectUrl);
        } else {
          window.location.href = redirectUrl;
        }
      }
    }, REDIRECT_DELAY);
  };
};

export const useHomeClubRedirect = (userFlow: SignupFlow) => {
  const { p } = useLocalization();
  const scheduleTimeout = useCancellableTimeout();

  return () => {
    scheduleTimeout(() => {
      const translationKey =
        userFlow === SignupFlow.ContentHomeClub
          ? 'h24_home_club_sub_failed_url'
          : 'h24_my_overview_sub_failed_url';
      window.location.href = p.t(translationKey);
    }, REDIRECT_DELAY);
  };
};
